import { useMemo } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";

const TABS = [
  { to: "/planes",            label: "El mes" },
  { to: "/planes/calendario", label: "Calendario" },
  { to: "/planes/feed",       label: "Feed" },
  // v8.6.22 · Fase 24 — mudado de "Resultados": no es contenido
  // retrospectivo, es algo que el cliente necesita ver venir.
  { to: "/planes/coberturas", label: "Coberturas" },
  // Lo que la agencia compartió: la presentación del plan, vista acá mismo (sin links de Drive).
  { to: "/planes/presentaciones", label: "Presentaciones" },
];

// v8.6.22 · Fase 25 (auditoría) — startsWith por substring podía dar un
// falso positivo si un :grupoId real empezara con el mismo texto que una
// pestaña estática (ej. "/planes/coberturasXYZ" activaría "Coberturas").
// No explotable con el formato real de grupoId (GRP-...), pero se corrige
// para no depender de esa suposición — match por segmento completo.
function matchesPath(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * PlanLayout · v8.5.55 · piel — título "Plan" + pestañas subrayadas (15px,
 * 2px --ink en la activa, no pills) + selector de período a la derecha.
 * Antes delegaba todo a ZoneNav (nav vertical de 200px compartida con
 * Cuenta/Trabajo/Resultados) — Plan pasa a un shell propio porque el spec
 * pide un encabezado font-display + tabs horizontales + selector, una forma
 * que ZoneNav no tiene y que las otras 3 zonas no pidieron cambiar.
 *
 * El período (`?periodo=YYYY-MM`) vive en la URL para sobrevivir el cambio
 * de pestaña y la vuelta desde una pieza (atrás del navegador). Se expone a
 * las pantallas hijas via <Outlet context>. No es literalmente el
 * componente FiltroFecha (day-range presets) — un mes no es un rango de
 * días, así que el control es un <input type="month"> propio, mismo
 * criterio visual (mono, borde 1px) que el resto de la piel.
 */
export default function PlanLayout() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const periodo = params.get("periodo") || mesActual();

  const setPeriodo = (v) => {
    // v8.6.24 · Fase 62 — sin este chequeo, PlanDetalle (que llama setPeriodo
    // desde un efecto con `setPeriodo` en las deps) entraba en un bucle
    // infinito de replace-navigation: cada navegación recreaba setPeriodo →
    // el efecto se re-disparaba → otra navegación. Safari corta a las 100
    // llamadas/10s de history.replaceState y tira "Algo salió mal".
    if (params.get("periodo") === v) return;
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("periodo", v);
      return next;
    }, { replace: true });
  };

  const tabsConPeriodo = useMemo(() =>
    TABS.map(t => ({ ...t, href: `${t.to}?periodo=${periodo}` })),
  [periodo]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="h1-portal text-[var(--ink)]">Plan</h1>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)]">Período</span>
          <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)}
                 className="px-4 h-10 bg-[var(--surface)] rounded-full text-sm font-semibold text-[var(--ink-2)] focus:outline-none focus:border-[var(--accent)]"/>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide" style={{ WebkitMaskImage: "linear-gradient(to right, #000 90%, transparent)", maskImage: "linear-gradient(to right, #000 90%, transparent)" }}>
        {tabsConPeriodo.map(t => (
          <NavLink key={t.to} to={t.href}
                   className={() => {
                     // v8.6.22 · Fase 24 — el regex de "El mes" matcheaba
                     // CUALQUIER sub-path de un segmento (pensado para
                     // /planes/:grupoId, el detalle de un plan) — sin esta
                     // exclusión, /planes/coberturas activaba "El mes" Y
                     // "Coberturas" a la vez.
                     const active = t.to === "/planes"
                       ? location.pathname === "/planes" ||
                         (/^\/planes\/[^/]+$/.test(location.pathname) &&
                          !TABS.some(tab => tab.to !== "/planes" && matchesPath(location.pathname, tab.to)))
                       : matchesPath(location.pathname, t.to);
                     return `inline-flex shrink-0 items-center whitespace-nowrap h-10 px-[18px] rounded-full text-[15px] transition-colors ${
                       active ? "bg-[var(--ink)] text-[var(--surface)] font-extrabold" : "bg-[var(--surface)] text-[var(--ink-3)] font-semibold hover:bg-[var(--line)]"
                     }`;
                   }}>
            {t.label}
          </NavLink>
        ))}
      </div>

      <Outlet context={{ periodo, setPeriodo }}/>
    </div>
  );
}
